import { useMutation, useQuery } from "@tanstack/react-query";
import to from "await-to-js";
import { ApiResponseObject } from "../../types";
import { settingsKeys } from "./settings.keys";
import { settingsService } from "./settings.service";
/**
 * Hook for fetching company details.
 *
 * @param {Object} options - Configuration options.
 * @param {boolean} [options.enabled] - If true, enables the query.
 * @returns {UseQueryResult} The result of the query containing company details.
 */
export function useCompanyDetails({ enabled }: { enabled?: boolean } = {}) {
  return useQuery({
    ...settingsKeys.companyDetails(),
    queryFn: settingsService.getCompanyDetails,
    select: (response) => response.data,
    enabled,
  });
}
/**
 * Hook for fetching API settings.
 *
 * @param {Object} options - Configuration options.
 * @param {boolean} [options.enabled] - If true, enables the query.
 * @returns {UseQueryResult} The result of the query containing API settings.
 */
export function useApiSettings({ enabled }: { enabled?: boolean } = {}) {
  return useQuery({
    ...settingsKeys.apiSettings(),
    queryFn: async () => {
      const [err, res] = await to(settingsService.getApiSettings());
      if (err || !res.success) throw new Error(res?.errorMessage);
      return res.data;
    },
    enabled,
  });
}
/**
 * Hook for editing company notification email.
 *
 * @param {Object} options - Configuration options.
 * @param {Function} options.onSettled - Function that executes after the mutation is either successful or fails.
 * @param {Function} options.onError - Function that executes if the mutation fails.
 * @returns {UseMutationResult} The result of the mutation for editing company email.
 */
export function useEditCompanyEmail({
  onSettled,
  onError,
}: {
  onSettled: (
    data: ApiResponseObject<string> | undefined,
    variables: string | undefined
  ) => void;
  onError: () => void;
}) {
  return useMutation({
    mutationFn: settingsService.editNotificationEmail,
    onSettled: (data, _error, variables) => onSettled(data, variables),
    onError,
  });
}
/**
 * Hook for toggling the notification status.
 *
 * @param {Object} options - Configuration options.
 * @param {Function} options.onSettled - Function that executes after the mutation is either successful or fails.
 * @param {Function} options.onError - Function that executes if the mutation fails.
 * @returns {UseMutationResult} The result of the mutation for toggling notification status.
 */
export function useToggleNotificationStatus({
  onSettled,
  onError,
}: {
  onSettled: (
    data: ApiResponseObject<boolean> | undefined,
    variables: boolean
  ) => void;
  onError: () => void;
}) {
  return useMutation({
    mutationFn: settingsService.getPromeniStatusEmailNotifikacija,
    onSettled: (data, _error, variables) => onSettled(data, variables),
    onError,
  });
}

export function useRefreshApiKey({
  onSettled,
  onError,
}: {
  onSettled: (data: ApiResponseObject<string> | undefined) => void;
  onError: () => void;
}) {
  return useMutation({
    mutationFn: settingsService.refreshApiKey,
    onSettled,
    onError,
  });
}

export function useEditNotificationStatus({
  onSettled,
  onError,
}: {
  onSettled: (
    data: ApiResponseObject<boolean> | undefined,
    variables: boolean
  ) => void;
  onError: () => void;
}) {
  return useMutation({
    mutationFn: settingsService.editNotificationStatus,
    onSettled: (data, _error, variables) => onSettled(data, variables),
    onError,
  });
}
